import type { StockTransfertDetail } from '../logiciel-caisse/logiciel-caisse-stock-transfert.service';
import { readLcTransfertBarcodeQuantites } from '../logiciel-caisse/logiciel-caisse-stock-transfert-detail-document-map.util';
import {
  lcStockTransfertBarcodeKey,
  lcStockTransfertBarcodeLooseKey,
} from '../logiciel-caisse/logiciel-caisse-stock-transfert-modal-detail.util';
import {
  mapErpLignesToPvRegularisationItems,
  PvEcartDocumentItemForRegularisation,
} from './pv-ecart-regularisation-flow.util';

export type PvEcartQtyBreakdownRow = {
  qteEnvoyee: number | null;
  qteRecue: number | null;
  /** Positif = reçu > envoyé (excédent), négatif = manquant. */
  ecartSigned: number;
};

function normalizeSens(raw: unknown): 'MANQUANT' | 'EXCEDENT' | null {
  const s = String(raw ?? '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (s === 'EXCEDENT') {
    return 'EXCEDENT';
  }
  if (s === 'MANQUANT') {
    return 'MANQUANT';
  }
  return null;
}

function inferSensFromDescription(description: unknown): 'MANQUANT' | 'EXCEDENT' | null {
  const blob = String(description ?? '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (blob.includes('INFERIEURE')) {
    return 'MANQUANT';
  }
  if (blob.includes('SUPERIEURE')) {
    return 'EXCEDENT';
  }
  return null;
}

function resolveSens(it: PvEcartDocumentItemForRegularisation, ligne: Record<string, unknown>) {
  return (
    normalizeSens(it.sens) ??
    normalizeSens(ligne['sens']) ??
    inferSensFromDescription(it.description ?? ligne['description'])
  );
}

function parseOptionalQty(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const n = Math.trunc(Number(value));
  return Number.isFinite(n) ? n : null;
}

function ligneVariantKey(ligne: Record<string, unknown>): string {
  const pid = Number(ligne['product_id']) || 0;
  const c = String(ligne['couleur'] ?? '')
    .trim()
    .toLowerCase();
  const t = String(ligne['taille'] ?? '')
    .trim()
    .toLowerCase();
  return `${pid}|${c}|${t}`;
}

function sumQtyForKey(
  lignes: Array<Record<string, unknown>> | null | undefined,
  key: string
): number {
  let total = 0;
  for (const l of lignes || []) {
    if (ligneVariantKey(l) !== key) {
      continue;
    }
    const q = Math.trunc(Number(l['qnt'] ?? l['quantity']) || 0);
    if (q > 0) {
      total += q;
    }
  }
  return total;
}

/** Quantités envoyée / reçue / écart signé pour une ligne PV ERP. */
export function buildPvEcartQtyBreakdownFromErpLigne(
  ligne: Record<string, unknown>,
  options?: {
    btLignes?: Array<Record<string, unknown>>;
    brLignes?: Array<Record<string, unknown>>;
  }
): PvEcartQtyBreakdownRow | null {
  const mapped = mapErpLignesToPvRegularisationItems([ligne]);
  if (!mapped.length) {
    return null;
  }
  const it = mapped[0];
  const ecartAbs = Math.abs(Math.trunc(Number(it.quantity ?? it.qnt) || 0));
  if (ecartAbs <= 0) {
    return null;
  }
  const sens = resolveSens(it, ligne);
  const ecartSigned =
    sens === 'EXCEDENT' ? ecartAbs : sens === 'MANQUANT' ? -ecartAbs : ecartAbs;

  let qteEnvoyee =
    parseOptionalQty(ligne['qte_envoyee']) ??
    parseOptionalQty(ligne['quantite_envoyee']);
  let qteRecue =
    parseOptionalQty(ligne['qte_recue']) ?? parseOptionalQty(ligne['quantite_recue']);

  const key = ligneVariantKey(ligne);
  const btLignes = options?.btLignes;
  const brLignes = options?.brLignes;
  if ((qteEnvoyee == null || qteRecue == null) && btLignes?.length) {
    const btQ = sumQtyForKey(btLignes, key);
    const brQ = brLignes?.length ? sumQtyForKey(brLignes, key) : 0;
    if (btQ > 0 || brQ > 0) {
      qteEnvoyee = btQ;
      qteRecue = brQ;
    }
  }

  if (qteEnvoyee != null && qteRecue == null && sens === 'MANQUANT') {
    qteRecue = qteEnvoyee - ecartAbs;
  } else if (qteEnvoyee != null && qteRecue == null && sens === 'EXCEDENT') {
    qteRecue = qteEnvoyee + ecartAbs;
  } else if (qteRecue != null && qteEnvoyee == null && sens === 'MANQUANT') {
    qteEnvoyee = qteRecue + ecartAbs;
  } else if (qteRecue != null && qteEnvoyee == null && sens === 'EXCEDENT') {
    qteEnvoyee = Math.max(0, qteRecue - ecartAbs);
  }

  return { qteEnvoyee, qteRecue, ecartSigned };
}

export function formatPvEcartQtyDisplay(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) {
    return '—';
  }
  return String(Math.trunc(Number(value)));
}

export type PvEcartProductLineForLcMatch = {
  type?: string;
  product_id?: number | null;
  code_barre?: string | null;
  reference?: string | null;
  couleur?: string | null;
  taille?: string | null;
};

function lcQtyMapsFromMergedDetail(detail: StockTransfertDetail): {
  envoye: Map<string, number>;
  recu: Map<string, number>;
} {
  const envoye = new Map<string, number>();
  const recu = new Map<string, number>();
  const headerIsReception = Number(detail.is_reception) || 0;
  for (const lg of detail.lignes || []) {
    for (const bc of lg.barcodes || []) {
      const hasMergedQty =
        bc.quantite_envoyee != null && bc.quantite_recue != null;
      const { qteEnvoyee, qteRecue } = hasMergedQty
        ? {
            qteEnvoyee: Math.trunc(Number(bc.quantite_envoyee) || 0),
            qteRecue: Math.trunc(Number(bc.quantite_recue) || 0),
          }
        : readLcTransfertBarcodeQuantites(bc, headerIsReception);
      for (const key of [lcStockTransfertBarcodeKey(bc), lcStockTransfertBarcodeLooseKey(bc)]) {
        if (qteEnvoyee > 0) {
          envoye.set(key, Math.max(envoye.get(key) || 0, qteEnvoyee));
        }
        if (qteRecue > 0) {
          recu.set(key, Math.max(recu.get(key) || 0, qteRecue));
        }
      }
    }
  }
  return { envoye, recu };
}

function lookupLcQty(map: Map<string, number>, line: PvEcartProductLineForLcMatch): number {
  const bc = {
    product_id: line.product_id ?? null,
    code_barre: String(line.code_barre ?? '').trim() || null,
    couleur: line.couleur ?? null,
    taille: line.taille ?? null,
  };
  const strict = map.get(lcStockTransfertBarcodeKey(bc));
  if (strict != null) {
    return strict;
  }
  return map.get(lcStockTransfertBarcodeLooseKey(bc)) ?? 0;
}

/**
 * Qté envoyée / reçue depuis stock_transfert LC (phases fusionnées) pour le tableau PV.
 */
export function applyPvEcartQtyBreakdownFromLcTransfertDetail(
  items: PvEcartProductLineForLcMatch[],
  detail: StockTransfertDetail,
  breakdownMap: Map<number, PvEcartQtyBreakdownRow>
): void {
  const { envoye, recu } = lcQtyMapsFromMergedDetail(detail);
  items.forEach((item, index) => {
    if (item.type && item.type !== 'product') {
      return;
    }
    const qEnv = lookupLcQty(envoye, item);
    const qRec = lookupLcQty(recu, item);
    if (qEnv <= 0 && qRec <= 0) {
      return;
    }
    const ecartSigned = qRec - qEnv;
    breakdownMap.set(index, {
      qteEnvoyee: qEnv > 0 ? qEnv : null,
      qteRecue: qRec > 0 ? qRec : null,
      ecartSigned,
    });
  });
}
